import { AutomationTriggerEvent, ServiceCategory } from '@prisma/client';
import {
  AUTOMATION_SERVICE_CATEGORY_LABEL,
  AUTOMATION_TRIGGER_EVENT_LABEL,
} from '../../constants/automations';
import {
  AutomationDelay,
  AutomationDelayUnit,
  AutomationFormValues,
  AutomationListItem,
  AutomationRecipe,
  CreateAutomationRequest,
} from '../../types/marketing/automations';
import { pluralize } from '../../utils/global/helper';
import { BusinessService } from '../../types/businesses';
import { PocketflowsEmailAddress } from '../../types/marketing';
import { isEmailSenderDisabledInUi } from '.';

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;

const SECONDS_PER_UNIT: Record<AutomationDelayUnit, number> = {
  MINUTES: MINUTE,
  HOURS: HOUR,
  DAYS: DAY,
  WEEKS: WEEK,
};

export function getDelaySeconds(delay: AutomationDelay): number | null {
  if (delay.timing === 'IMMEDIATE') return 0;
  if (!/^[1-9]\d*$/.test(delay.amount)) return null;
  return Number(delay.amount) * SECONDS_PER_UNIT[delay.unit];
}

function formatCount(count: number, unit: string) {
  return `${count} ${pluralize(unit, count)}`;
}

function getDelayLabel(delaySeconds: number | null) {
  if (delaySeconds === null) return 'after a delay';
  if (delaySeconds === 0) return 'immediately';
  if (delaySeconds % WEEK === 0) {
    const weeks = delaySeconds / WEEK;
    return formatCount(weeks, 'week');
  }
  if (delaySeconds % DAY === 0) {
    const days = delaySeconds / DAY;
    return formatCount(days, 'day');
  }
  if (delaySeconds % HOUR === 0) {
    const hours = delaySeconds / HOUR;
    return formatCount(hours, 'hour');
  }
  const minutes = Math.round(delaySeconds / MINUTE);
  return formatCount(minutes, 'minute');
}

export function describeTrigger({
  triggerEvent,
  delaySeconds,
  serviceCategories,
}: {
  triggerEvent: AutomationTriggerEvent;
  delaySeconds: number | null;
  serviceCategories: ServiceCategory[];
}) {
  const label = AUTOMATION_TRIGGER_EVENT_LABEL[triggerEvent];
  const delay = getDelayLabel(delaySeconds);
  const parts = delaySeconds ? [`${label} + ${delay}`] : [label, delay];
  if (serviceCategories.length > 0) {
    parts.push(
      serviceCategories
        .map(category => AUTOMATION_SERVICE_CATEGORY_LABEL[category])
        .join(', '),
    );
  }
  return parts.join(' · ');
}

export function getRecipeDelay(
  recipe: AutomationRecipe,
): AutomationFormValues['delay'] {
  if (!recipe.delaySeconds) {
    return { timing: 'IMMEDIATE', amount: '', unit: 'DAYS' };
  }
  const unit: AutomationDelayUnit =
    recipe.delaySeconds % WEEK === 0
      ? 'WEEKS'
      : recipe.delaySeconds % DAY === 0
      ? 'DAYS'
      : recipe.delaySeconds % HOUR === 0
      ? 'HOURS'
      : 'MINUTES';
  return {
    timing: 'AFTER',
    amount: String(recipe.delaySeconds / SECONDS_PER_UNIT[unit]),
    unit,
  };
}

/// A validated custom domain wins; otherwise the shared address most shops have
export function getDefaultEmailAddressId(
  emailAddresses: PocketflowsEmailAddress<true>[] | null | undefined,
) {
  const usable =
    emailAddresses?.filter(email => !isEmailSenderDisabledInUi(email)) ?? [];
  const customDomain = usable.find(
    email => !!email.custom_domain && typeof email.custom_domain === 'object',
  );
  const shared = usable.find(email => !email.custom_domain);
  return (customDomain ?? shared ?? usable[0])?.id ?? '';
}

export function getEmptyAutomationFormValues(): AutomationFormValues {
  return {
    recipeId: null,
    name: '',
    channel: 'SMS',
    triggerEvent: 'CHECKOUT_COMPLETED',
    delay: { timing: 'IMMEDIATE', amount: '', unit: 'DAYS' },
    serviceCategories: [],
    emailAddressId: '',
    templateId: null,
  };
}

export function recipeToFormValues(
  recipe: AutomationRecipe,
): AutomationFormValues {
  return {
    recipeId: recipe.id,
    name: recipe.title,
    channel: recipe.channel,
    triggerEvent: recipe.triggerEvent,
    delay: getRecipeDelay(recipe),
    serviceCategories: [...recipe.serviceCategories],
    emailAddressId: '',
    templateId: null,
  };
}

export function toCreateAutomationRequest(
  marketingBusinessId: string,
  values: AutomationFormValues,
): CreateAutomationRequest | null {
  const delaySeconds = getDelaySeconds(values.delay);
  if (delaySeconds === null || values.templateId === null) return null;
  return {
    marketingBusinessId,
    recipeId: values.recipeId ?? undefined,
    name: values.name.trim(),
    channel: values.channel,
    triggerEvent: values.triggerEvent,
    delaySeconds,
    serviceCategories: values.serviceCategories,
    emailAddressId: values.emailAddressId || undefined,
    templateId: values.templateId,
  };
}

export function getAutomationsByRecipeId(automations: AutomationListItem[]) {
  const byRecipeId = new Map<string, AutomationListItem>();
  automations.forEach(automation => {
    if (automation.recipeId) byRecipeId.set(automation.recipeId, automation);
  });
  return byRecipeId;
}

// Tattoo is never added under Other Services, so it counts as offered everywhere
const ALWAYS_OFFERED_CATEGORIES: ServiceCategory[] = ['TATTOO'];

export function filterRecipesByServices(
  recipes: AutomationRecipe[],
  services: Pick<BusinessService, 'serviceCategory'>[],
) {
  const offered = new Set<ServiceCategory>([
    ...ALWAYS_OFFERED_CATEGORIES,
    ...services.map(service => service.serviceCategory),
  ]);
  return recipes.filter(
    recipe =>
      recipe.serviceCategories.length === 0 ||
      recipe.serviceCategories.some(category => offered.has(category)),
  );
}
